import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useAuth, hasRole } from '@/lib/auth';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PageHeader } from '@/components/PageHeader';
import { DictationButton } from '@/components/DictationButton';
import { AIWandButton } from '@/components/AIWandButton';
import { fmtMoney } from '@/lib/format';
import {
  Check, ChevronRight, ChevronLeft, Plus, Search, X, User, Package, CreditCard, FileCheck, Loader2, Trash2, Sparkles,
} from 'lucide-react';

const STEPS = ['client', 'services', 'payment', 'generate'];

function StepPill({ index, current, label, icon: Icon }) {
  const status = index < current ? 'done' : index === current ? 'active' : 'todo';
  const cls = status === 'active'
    ? 'bg-slate-900 text-white border-slate-900'
    : status === 'done'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-white text-slate-500 border-slate-200';
  return (
    <div className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm ${cls}`} data-testid={`step-${STEPS[index]}`}>
      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold ${status === 'active' ? 'bg-white text-slate-900' : status === 'done' ? 'bg-emerald-100' : 'bg-slate-100'}`}>
        {status === 'done' ? <Check className="h-3.5 w-3.5" /> : index + 1}
      </div>
      <Icon className="h-4 w-4" />
      <span className="font-medium">{label}</span>
    </div>
  );
}

export default function QuickReceipt() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stripeCfg, setStripeCfg] = useState({ bnpl_enabled: true, bnpl_threshold_usd: 500 });
  // Tanda 7: Issuer profiles
  const [issuerProfiles, setIssuerProfiles] = useState([]);
  const [selectedIssuerId, setSelectedIssuerId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [newClient, setNewClient] = useState({ full_name: '', email: '', phone: '', client_matter_id: '', case_name: '' });
  const [items, setItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('later');
  const [visibleNotes, setVisibleNotes] = useState('');
  // Tanda 8: reminder cadence for the invoice — sent automatically until paid
  const [reminderCadence, setReminderCadence] = useState('none');
  // Tanda 16: Manual invoice-level discount (one at a time: % or amount)
  const [manualDiscount, setManualDiscount] = useState({ type: 'percent', value: '', note: '' });
  const [manualDiscountEnabled, setManualDiscountEnabled] = useState(false);
  // Tanda 16: Manual early-payment discount (per-invoice override)
  const [manualEarly, setManualEarly] = useState({
    enabled: false, type: 'percent', value: '', deadline: '', note: '',
  });
  const [busy, setBusy] = useState(false);
  const [createdReceipt, setCreatedReceipt] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/clients'),
      api.get('/services'),
      api.get('/settings'),
    ]).then(([c, s, st]) => {
      setClients(c.data);
      setServices(s.data);
      setSettings(st.data);
    }).catch(() => {});
    // Load Stripe config (BNPL threshold) — non-blocking
    api.get('/payments/stripe/config')
      .then(r => setStripeCfg({
        bnpl_enabled: r.data.bnpl_enabled ?? true,
        bnpl_threshold_usd: Number(r.data.bnpl_threshold_usd ?? 500),
      }))
      .catch(() => {});
    // Tanda 7: Load issuer profiles + preselect user default or global default
    api.get('/issuer-profiles')
      .then(r => {
        const items = (r.data?.items || []).filter(p => p.active);
        setIssuerProfiles(items);
        // Choose: current user preferred → default → first active
        const stored = localStorage.getItem('firmpay_default_issuer_profile_id');
        let picked = null;
        if (stored) picked = items.find(p => p.id === stored);
        if (!picked) picked = items.find(p => p.is_default);
        if (!picked && items.length) picked = items[0];
        if (picked) setSelectedIssuerId(picked.id);
      })
      .catch(() => {});
  }, []);

  // Pick up dictation payload stashed by the AI chat before navigation.
  // We wait until `clients` are loaded so fuzzy match can auto-select.
  useEffect(() => {
    if (!clients?.length) return;
    try {
      const raw = sessionStorage.getItem('ezdockit.dictation.pending');
      if (!raw) return;
      const stashed = JSON.parse(raw);
      // Only apply if fresh (< 60s)
      if (!stashed?.payload || Date.now() - (stashed.ts || 0) > 60_000) {
        sessionStorage.removeItem('ezdockit.dictation.pending');
        return;
      }
      sessionStorage.removeItem('ezdockit.dictation.pending');
      applyDictation(stashed.payload);
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const filteredClients = clientSearch
    ? clients.filter(c => (c.full_name + ' ' + (c.email||'') + ' ' + (c.phone||'') + ' ' + (c.client_matter_id||'')).toLowerCase().includes(clientSearch.toLowerCase()))
    : clients;

  const totals = (() => {
    const taxEnabled = !!settings?.tax_enabled;
    const stateRate = taxEnabled ? Number(settings?.tax_state_rate ?? 0.105) : 0;
    const muniRate = taxEnabled ? Number(settings?.tax_municipal_rate ?? 0.01) : 0;
    // Fallback to combined tax_rate only if split rates are both zero (legacy)
    const combined = stateRate + muniRate;
    const useLegacy = combined <= 0 && taxEnabled;
    const legacyRate = useLegacy ? Number(settings?.tax_rate ?? 0) : 0;

    let subtotal = 0, disc = 0, taxState = 0, taxMuni = 0, taxLegacy = 0;
    items.forEach(it => {
      const gross = Number(it.quantity || 0) * Number(it.unit_price || 0);
      const d = Number(it.discount || 0);
      const net = Math.max(0, gross - d);
      subtotal += gross;
      disc += d;
      if (it.taxable && taxEnabled) {
        if (useLegacy) {
          taxLegacy += net * legacyRate;
        } else {
          taxState += net * stateRate;
          taxMuni += net * muniRate;
        }
      }
    });
    const tax = taxState + taxMuni + taxLegacy;
    return {
      subtotal,
      disc,
      taxState: Math.round(taxState * 100) / 100,
      taxMuni: Math.round(taxMuni * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round((subtotal - disc + tax) * 100) / 100,
      stateRate, muniRate,
    };
  })();

  const addService = (svc) => {
    setItems(prev => [...prev, {
      service_id: svc.id,
      service_name: svc.name,
      description: svc.description || '',
      quantity: 1,
      unit_price: svc.default_price,
      discount: 0,
      taxable: svc.taxable,
    }]);
  };

  /**
   * applyDictation — hydrates form state from AI-parsed voice dictation.
   * The backend returns fuzzy-matched client + service candidates so we
   * can pre-select confident matches and skip UI steps when possible.
   */
  const applyDictation = (parsed) => {
    if (!parsed) return;

    // Client: use confident auto-match if any, else set search box.
    if (parsed.client_id) {
      const found = clients.find(c => c.id === parsed.client_id);
      if (found) { setSelectedClient(found); setStep(prev => Math.max(prev, 1)); }
    } else if (parsed.client_candidates?.length) {
      const top = parsed.client_candidates[0];
      if (top.score >= 0.6) {
        const found = clients.find(c => c.id === top.id);
        if (found) { setSelectedClient(found); setStep(prev => Math.max(prev, 1)); }
      } else if (parsed.client_name_guess) {
        setClientSearch(parsed.client_name_guess);
      }
    } else if (parsed.client_name_guess) {
      setClientSearch(parsed.client_name_guess);
    }

    // Line items: prepend dictated items (avoid wiping existing user work).
    const dictatedItems = (parsed.line_items || []).map(it => {
      const svcCandidate = (it.service_candidates || [])[0];
      const useCandidate = svcCandidate && svcCandidate.score >= 0.75;
      return {
        service_id: it.service_id || (useCandidate ? svcCandidate.id : null),
        service_name: useCandidate ? svcCandidate.name : it.service_name,
        description: it.description || '',
        quantity: Number(it.quantity) || 1,
        unit_price: Number(it.unit_price) || (useCandidate ? Number(svcCandidate.unit_price || 0) : 0),
        discount: 0,
        taxable: false,
      };
    });
    if (dictatedItems.length) {
      setItems(prev => [...dictatedItems, ...prev]);
      if (step < 1) setStep(1);
    }

    // Payment method mapping (backend may return 'cash'/'card'/'check'/'transfer'/'ath_movil'/'pending')
    const pmMap = { pending: 'later', cash: 'cash', card: 'card', check: 'check', transfer: 'transfer', ath_movil: 'ath_movil' };
    if (parsed.payment_method && pmMap[parsed.payment_method]) {
      setPaymentMethod(pmMap[parsed.payment_method]);
    }

    // Notes
    if (parsed.visible_notes) {
      setVisibleNotes(prev => prev ? `${prev}\n${parsed.visible_notes}` : parsed.visible_notes);
    }

    // Manual discount
    if (parsed.manual_discount_type && parsed.manual_discount_value != null) {
      setManualDiscountEnabled(true);
      setManualDiscount({
        type: parsed.manual_discount_type === 'amount' ? 'amount' : 'percent',
        value: String(parsed.manual_discount_value),
        note: 'Aplicado por dictado',
      });
    }
  };

  const addCustom = () => {
    if (!settings?.allow_custom_services) return toast.error('Servicios personalizados no permitidos');
    setItems(prev => [...prev, {
      service_name: 'Servicio personalizado', description: '', quantity: 1, unit_price: 0, discount: 0, taxable: false,
    }]);
  };

  const updateItem = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const createClientSubmit = async () => {
    if (!newClient.full_name.trim()) return toast.error('Nombre requerido');
    try {
      const { data } = await api.post('/clients', newClient);
      setClients(prev => [data, ...prev]);
      setSelectedClient(data);
      setShowCreateClient(false);
      setNewClient({ full_name: '', email: '', phone: '', client_matter_id: '', case_name: '' });
      toast.success('Cliente creado');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const canNext = () => {
    if (step === 0) return !!selectedClient;
    if (step === 1) return items.length > 0 && items.every(it => it.service_name && Number(it.unit_price) >= 0);
    if (step === 2) return !!paymentMethod;
    return true;
  };

  const generateReceipt = async () => {
    setBusy(true);
    try {
      const body = {
        client_id: selectedClient.id,
        issuer_profile_id: selectedIssuerId || null,
        line_items: items.map(it => ({
          service_id: it.service_id || null,
          service_name: it.service_name,
          description: it.description,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          discount: Number(it.discount || 0),
          taxable: !!it.taxable,
        })),
        payment_method: 'pending',
        visible_notes: visibleNotes,
        reminder_cadence: reminderCadence || 'none',
        // Tanda 16: manual invoice-level discount
        manual_discount_type: manualDiscountEnabled && Number(manualDiscount.value) > 0 ? manualDiscount.type : null,
        manual_discount_value: manualDiscountEnabled ? Number(manualDiscount.value || 0) : 0,
        manual_discount_note: manualDiscountEnabled ? (manualDiscount.note || null) : null,
        // Tanda 16: manual early-payment discount
        manual_early_discount_enabled: !!manualEarly.enabled,
        manual_early_discount_type: manualEarly.enabled ? manualEarly.type : null,
        manual_early_discount_value: manualEarly.enabled ? Number(manualEarly.value || 0) : 0,
        manual_early_discount_deadline: manualEarly.enabled ? (manualEarly.deadline || null) : null,
        manual_early_discount_note: manualEarly.enabled ? (manualEarly.note || null) : null,
      };
      const { data } = await api.post('/receipts', body);
      if (!data || !data.id) {
        toast.error('Respuesta inválida del servidor al crear la factura');
        return;
      }
      setCreatedReceipt(data);
      toast.success(`Factura ${data.receipt_number} creada`);

      // Fire payment based on method
      if (paymentMethod === 'stripe' || paymentMethod === 'klarna') {
        const origin = window.location.origin;
        try {
          const { data: checkout } = await api.post('/payments/stripe/create-checkout', {
            receipt_id: data.id,
            origin_url: origin,
            payment_method: paymentMethod === 'klarna' ? 'klarna' : 'auto',
          });
          // Defensive: if checkout.url is missing, fall back to invoice detail
          // instead of trying to `window.location.href = undefined` (which
          // navigates to /undefined and shows a blank SPA route on prod).
          if (!checkout?.url || typeof checkout.url !== 'string') {
            toast.error('Stripe no devolvió una URL válida — revise las claves en Configuración.');
            navigate(`/recibos/${data.id}`);
            return;
          }
          toast.info(paymentMethod === 'klarna' ? 'Redirigiendo a Klarna…' : 'Redirigiendo a Stripe…');
          window.location.href = checkout.url;
          return;
        } catch (err) {
          const msg = err?.response?.data?.detail || 'No se pudo iniciar el pago con Stripe';
          toast.error(String(msg));
          navigate(`/recibos/${data.id}`);
          return;
        }
      } else if (paymentMethod === 'ath_movil') {
        navigate(`/recibos/${data.id}?pay=ath`);
      } else if (['cash', 'check', 'bank_transfer'].includes(paymentMethod)) {
        try {
          await api.post('/payments/manual', {
            receipt_id: data.id,
            method: paymentMethod,
            amount: data.total,
            reference: '',
          });
          toast.success('Pago manual registrado');
        } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo registrar pago manual'); }
        navigate(`/recibos/${data.id}`);
      } else if (paymentMethod === 'link') {
        try {
          const { data: link } = await api.post('/payments/link', { receipt_id: data.id, origin_url: window.location.origin, channel: 'copy' });
          if (link?.link) {
            try { await navigator.clipboard.writeText(link.link); toast.success('Link de pago copiado al portapapeles'); } catch { toast.info(link.link); }
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'No se pudo generar el link de pago');
        }
        navigate(`/recibos/${data.id}`);
      } else if (paymentMethod === 'later') {
        toast.success('Factura creada. Puede enviar el link de pago o cobrar cuando desee desde la vista de la factura.');
        navigate(`/recibos/${data.id}`);
      } else {
        navigate(`/recibos/${data.id}`);
      }
    } catch (e) {
      // Log to console so devs can see the real error even when toast fails
      // eslint-disable-next-line no-console
      console.error('generateReceipt failed:', e);
      toast.error(e?.response?.data?.detail || e?.message || 'Error al generar factura');
    } finally { setBusy(false); }
  };

  const stepLabels = [t('qr_step_client'), t('qr_step_services'), t('qr_step_payment'), t('qr_step_generate')];
  const stepIcons = [User, Package, CreditCard, FileCheck];

  return (
    <div className="max-w-6xl">
      <PageHeader title={t('qr_title')} subtitle="Cree una factura en 4 pasos rápidos" />

      {/* Dictate invoice — voice-powered pre-fill via AI */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <DictationButton onParsed={applyDictation} testId="qr-dictate-btn" />
        <span className="text-xs text-slate-500">
          Diga los datos de la factura en voz alta y el asistente los llena por usted.
        </span>
      </div>

      {/* Tanda 7: Issuer profile selector — only visible when multiple exist */}
      {issuerProfiles.length > 1 && (
        <Card className="mb-4" data-testid="issuer-selector-card">
          <CardContent className="py-3 px-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-shrink-0 text-xs uppercase tracking-wide text-slate-500 font-medium">Emite como</div>
            <div className="flex flex-wrap gap-2 flex-1">
              {issuerProfiles.map(p => (
                <button
                  key={p.id}
                  onClick={() => { setSelectedIssuerId(p.id); localStorage.setItem('firmpay_default_issuer_profile_id', p.id); }}
                  data-testid={`issuer-select-${p.code}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border transition-colors ${selectedIssuerId === p.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}
                >
                  {p.logo_url ? (
                    <img src={p.logo_url} alt="" className="h-4 w-4 object-contain" />
                  ) : (
                    <span className={`font-mono text-[10px] px-1 rounded ${selectedIssuerId === p.id ? 'bg-white/20' : 'bg-slate-100'}`}>{p.code}</span>
                  )}
                  <span className="truncate max-w-[180px]">{p.name}</span>
                  {p.is_default && <span className="text-[9px] opacity-70">· default</span>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stepper */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        {STEPS.map((_, i) => (
          <StepPill key={i} index={i} current={step} label={stepLabels[i]} icon={stepIcons[i]} />
        ))}
      </div>
      <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5 mb-6" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Step 0: Client */}
          {step === 0 && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-900">Seleccionar cliente</h3>
                    <p className="text-xs text-slate-500">Busque por nombre, teléfono, email o matter #</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowCreateClient(true)} data-testid="create-client-button">
                    <Plus className="h-4 w-4 mr-1" /> {t('clients_new')}
                  </Button>
                </div>
                <div className="relative mb-4">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input placeholder="Buscar cliente..." value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="pl-9" data-testid="client-search-input" />
                </div>
                <div className="space-y-1.5 max-h-[400px] overflow-y-auto" data-testid="client-list">
                  {filteredClients.length === 0 && (
                    <div className="text-sm text-slate-500 p-4 text-center border border-dashed rounded-lg">Sin resultados</div>
                  )}
                  {filteredClients.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedClient(c)}
                      data-testid={`client-row-${c.id}`}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 hover:border-slate-400 transition-colors ${selectedClient?.id === c.id ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm text-slate-900">{c.full_name}</div>
                          <div className="text-xs text-slate-500">{[c.email, c.phone, c.client_matter_id].filter(Boolean).join(' · ')}</div>
                        </div>
                        {selectedClient?.id === c.id && <Check className="h-4 w-4 text-emerald-600" />}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 1: Services */}
          {step === 1 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-1">Servicios</h3>
                <p className="text-xs text-slate-500 mb-4">Agregue servicios del catálogo o personalizados</p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                  {services.slice(0, 12).map(s => (
                    <button
                      key={s.id}
                      onClick={() => addService(s)}
                      data-testid={`add-service-${s.id}`}
                      className="text-left rounded-lg border border-slate-200 hover:border-slate-400 hover:bg-slate-50 px-3 py-2"
                    >
                      <div className="text-xs text-slate-500">{s.category}</div>
                      <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 numeric">{fmtMoney(s.default_price)}</div>
                    </button>
                  ))}
                  {settings?.allow_custom_services && (
                    <button onClick={addCustom} data-testid="add-custom-service-button" className="text-left rounded-lg border border-dashed border-slate-300 hover:border-slate-500 hover:bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Personalizado</div>
                      <div className="text-sm font-medium text-slate-900 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Otro servicio</div>
                    </button>
                  )}
                </div>

                {items.length === 0 && (
                  <div className="text-sm text-slate-500 p-6 text-center border border-dashed rounded-lg">Aún no ha agregado servicios</div>
                )}

                <div className="space-y-2" data-testid="line-items-list">
                  {items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center border rounded-lg p-2">
                      <div className="col-span-12 sm:col-span-4">
                        <Input value={it.service_name} onChange={(e) => updateItem(idx, { service_name: e.target.value })} data-testid={`item-name-${idx}`} placeholder="Nombre" />
                        <div className="mt-1.5 flex items-start gap-1.5">
                          <Input value={it.description || ''} onChange={(e) => updateItem(idx, { description: e.target.value })} placeholder="Descripción (opcional)" className="text-xs flex-1" data-testid={`item-desc-${idx}`} />
                          <AIWandButton
                            value={it.description || it.service_name || ''}
                            onImproved={(text, data) => updateItem(idx, { description: text, ...(data?.suggested_service_name && !it.service_name?.trim() ? { service_name: data.suggested_service_name } : {}) })}
                            compact={true}
                            label="Mejorar descripción con IA"
                            testId={`ai-desc-wand-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="col-span-3 sm:col-span-1">
                        <Label className="text-[10px] text-slate-500">Cant.</Label>
                        <Input type="number" step="1" min="1" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`item-qty-${idx}`} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-[10px] text-slate-500">Precio</Label>
                        <Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} data-testid={`item-price-${idx}`} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-[10px] text-slate-500">Descuento</Label>
                        <Input type="number" step="0.01" min="0" value={it.discount || 0} onChange={(e) => updateItem(idx, { discount: e.target.value })} disabled={!settings?.allow_discounts} data-testid={`item-discount-${idx}`} />
                      </div>
                      <div className="col-span-3 sm:col-span-2 text-right font-medium numeric">
                        {fmtMoney(Number(it.quantity || 0) * Number(it.unit_price || 0) - Number(it.discount || 0))}
                      </div>
                      <div className="col-span-2 sm:col-span-1 text-right">
                        <Button variant="ghost" size="icon" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Payment method */}
          {step === 2 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-1">Método de pago</h3>
                <p className="text-xs text-slate-500 mb-4">Opcional — puede decidir cómo cobrar más tarde. Cree la factura y envíe el link de pago cuando quiera.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(() => {
                    const total = Number(totals?.total ?? 0);
                    const threshold = Number(stripeCfg.bnpl_threshold_usd ?? 500);
                    const klarnaEligible = !!stripeCfg.bnpl_enabled && total >= threshold;
                    return [
                    {
                      v: 'later',
                      label: 'Decidir después',
                      desc: 'Crear la factura ahora y enviar link de pago o cobrar luego',
                      recommended: true,
                    },
                    { v: 'stripe', label: t('pay_stripe'), desc: 'Tarjeta Visa/Mastercard vía Stripe' },
                    {
                      v: 'klarna',
                      label: 'Klarna — Pague en 4 plazos',
                      desc: klarnaEligible
                        ? '4 pagos sin interés vía Stripe'
                        : `Disponible para cobros de $${threshold.toFixed(0)}+`,
                      disabled: !klarnaEligible,
                      hidden: !stripeCfg.bnpl_enabled,
                      badge: 'BNPL',
                    },
                    { v: 'ath_movil', label: t('pay_ath'), desc: 'Botón de pago ATH Móvil', hidden: !settings?.ath_enabled },
                    { v: 'link', label: t('pay_link'), desc: 'Enviar link de pago Stripe' },
                    { v: 'cash', label: t('pay_cash'), desc: 'Pago en efectivo (manual)', hidden: !settings?.enable_manual_payments },
                    { v: 'check', label: t('pay_check'), desc: 'Cheque (manual)', hidden: !settings?.enable_manual_payments },
                    { v: 'bank_transfer', label: t('pay_bank'), desc: 'Transferencia (manual)', hidden: !settings?.enable_manual_payments },
                    ];
                  })().filter(o => !o.hidden).map(o => (
                    <button
                      key={o.v}
                      onClick={() => !o.disabled && setPaymentMethod(o.v)}
                      disabled={!!o.disabled}
                      data-testid={`payment-method-${o.v}`}
                      title={o.disabled ? o.desc : ''}
                      className={`text-left rounded-lg border px-4 py-3 transition-colors ${o.disabled ? 'opacity-50 cursor-not-allowed border-slate-200 bg-slate-50' : paymentMethod === o.v ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400'}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-sm text-slate-900">{o.label}</div>
                        {o.recommended && !o.disabled && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase tracking-wider">
                            Sugerido
                          </span>
                        )}
                        {o.badge && !o.disabled && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-pink-100 text-pink-800 uppercase tracking-wider">
                            {o.badge}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{o.desc}</div>
                    </button>
                  ))}
                </div>

                <div className="mt-6">
                  <Label htmlFor="notes">Notas visibles al cliente (opcional)</Label>
                  <Textarea id="notes" value={visibleNotes} onChange={(e) => setVisibleNotes(e.target.value)} rows={2} className="mt-1.5" data-testid="receipt-notes-input" />
                </div>

                {/* Tanda 16: Manual invoice-level discount */}
                <div className="mt-6 border-t border-slate-200 pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Descuento a la factura</div>
                      <div className="text-xs text-slate-500 mt-0.5">Opcional. En porcentaje o monto (uno a la vez).</div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={manualDiscountEnabled} onChange={(e) => setManualDiscountEnabled(e.target.checked)} className="h-4 w-4" data-testid="manual-discount-enable" />
                      <span>Aplicar descuento</span>
                    </label>
                  </div>
                  {manualDiscountEnabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                      <div>
                        <Label>Tipo</Label>
                        <Select value={manualDiscount.type} onValueChange={(v) => setManualDiscount(d => ({ ...d, type: v }))}>
                          <SelectTrigger className="mt-1" data-testid="manual-discount-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">Porcentaje (%)</SelectItem>
                            <SelectItem value="amount">Monto fijo ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Valor</Label>
                        <Input type="number" min="0" step="0.01" value={manualDiscount.value} onChange={(e) => setManualDiscount(d => ({ ...d, value: e.target.value }))} className="mt-1" data-testid="manual-discount-value" placeholder={manualDiscount.type === 'percent' ? 'Ej: 10' : 'Ej: 50.00'} />
                      </div>
                      <div>
                        <Label>Nota (opcional)</Label>
                        <Input value={manualDiscount.note} onChange={(e) => setManualDiscount(d => ({ ...d, note: e.target.value }))} className="mt-1" data-testid="manual-discount-note" placeholder="Motivo del descuento" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Tanda 16: Manual early-payment discount */}
                <div className="mt-6 border-t border-slate-200 pt-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Descuento por pronto pago</div>
                      <div className="text-xs text-slate-500 mt-0.5">Se aplica solo si el cliente paga en o antes de la fecha límite.</div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={manualEarly.enabled} onChange={(e) => setManualEarly(d => ({ ...d, enabled: e.target.checked }))} className="h-4 w-4" data-testid="manual-early-enable" />
                      <span>Ofrecer pronto pago</span>
                    </label>
                  </div>
                  {manualEarly.enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-3">
                      <div>
                        <Label>Tipo</Label>
                        <Select value={manualEarly.type} onValueChange={(v) => setManualEarly(d => ({ ...d, type: v }))}>
                          <SelectTrigger className="mt-1" data-testid="manual-early-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">Porcentaje (%)</SelectItem>
                            <SelectItem value="amount">Monto fijo ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Valor</Label>
                        <Input type="number" min="0" step="0.01" value={manualEarly.value} onChange={(e) => setManualEarly(d => ({ ...d, value: e.target.value }))} className="mt-1" data-testid="manual-early-value" />
                      </div>
                      <div>
                        <Label>Fecha límite</Label>
                        <Input type="date" value={manualEarly.deadline} onChange={(e) => setManualEarly(d => ({ ...d, deadline: e.target.value }))} className="mt-1" data-testid="manual-early-deadline" min={new Date().toISOString().slice(0,10)} />
                      </div>
                      <div>
                        <Label>Nota (opcional)</Label>
                        <Input value={manualEarly.note} onChange={(e) => setManualEarly(d => ({ ...d, note: e.target.value }))} className="mt-1" data-testid="manual-early-note" />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Generate */}
          {step === 3 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-1">Confirmar y generar</h3>
                <p className="text-xs text-slate-500 mb-4">Revise antes de generar la factura</p>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-medium">{selectedClient?.full_name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Items</span><span>{items.length}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Método</span><Badge variant="secondary">{paymentMethod}</Badge></div>
                  <div className="flex justify-between border-t pt-2 mt-2"><span className="text-slate-500">Subtotal</span><span className="numeric">{fmtMoney(totals.subtotal)}</span></div>
                  {totals.disc > 0 && <div className="flex justify-between"><span className="text-slate-500">Descuento</span><span className="numeric text-rose-700">-{fmtMoney(totals.disc)}</span></div>}
                  {totals.tax > 0 && totals.taxState > 0 && (
                    <div className="flex justify-between"><span className="text-slate-500">{settings?.tax_state_label || 'IVU Estatal'} ({(totals.stateRate*100).toFixed(1)}%)</span><span className="numeric">{fmtMoney(totals.taxState)}</span></div>
                  )}
                  {totals.tax > 0 && totals.taxMuni > 0 && (
                    <div className="flex justify-between"><span className="text-slate-500">{settings?.tax_municipal_label || 'IVU Municipal'} ({(totals.muniRate*100).toFixed(1)}%)</span><span className="numeric">{fmtMoney(totals.taxMuni)}</span></div>
                  )}
                  {totals.tax > 0 && totals.taxState === 0 && totals.taxMuni === 0 && (
                    <div className="flex justify-between"><span className="text-slate-500">{settings?.tax_name}</span><span className="numeric">{fmtMoney(totals.tax)}</span></div>
                  )}
                  <div className="flex justify-between border-t pt-2 text-base font-semibold"><span>Total</span><span className="numeric">{fmtMoney(totals.total)}</span></div>
                  {manualDiscountEnabled && Number(manualDiscount.value) > 0 && (
                    <div className="flex justify-between text-emerald-800" data-testid="review-manual-discount">
                      <span>
                        Descuento factura ({manualDiscount.type === 'percent' ? `${manualDiscount.value}%` : fmtMoney(Number(manualDiscount.value))})
                      </span>
                      <span className="numeric">
                        -{fmtMoney(manualDiscount.type === 'percent' ? (totals.total * Number(manualDiscount.value) / 100) : Number(manualDiscount.value))}
                      </span>
                    </div>
                  )}
                  {manualDiscountEnabled && Number(manualDiscount.value) > 0 && (
                    <div className="flex justify-between border-t pt-2 text-base font-semibold text-slate-900">
                      <span>Total con descuento</span>
                      <span className="numeric">
                        {(() => {
                          const disc = manualDiscount.type === 'percent'
                            ? totals.total * Number(manualDiscount.value) / 100
                            : Number(manualDiscount.value);
                          return fmtMoney(Math.max(0, totals.total - disc));
                        })()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Tanda 16: Manual early-payment discount preview */}
                {manualEarly.enabled && Number(manualEarly.value) > 0 && manualEarly.deadline && (
                  <div className="mt-4 flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800" data-testid="manual-early-preview">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 text-emerald-700 font-bold text-[10px]">%</span>
                    <div>
                      {(() => {
                        const baseTotal = totals.total - (manualDiscountEnabled && Number(manualDiscount.value) > 0
                          ? (manualDiscount.type === 'percent' ? totals.total * Number(manualDiscount.value) / 100 : Number(manualDiscount.value))
                          : 0);
                        const disc = manualEarly.type === 'percent'
                          ? baseTotal * Number(manualEarly.value) / 100
                          : Number(manualEarly.value);
                        const finalTotal = Math.max(0, baseTotal - disc);
                        return (
                          <>Si paga en o antes del <b>{manualEarly.deadline}</b> recibirá <b>{manualEarly.type === 'percent' ? `${manualEarly.value}%` : fmtMoney(Number(manualEarly.value))}</b> de descuento (
                          <span className="numeric">{fmtMoney(disc)}</span> menos → total <span className="numeric font-semibold">{fmtMoney(finalTotal)}</span>).</>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Tanda 9: Automatic early-payment discount preview (only if manual NOT set) */}
                {!manualEarly.enabled && settings?.early_payment_discount_enabled && settings?.early_payment_discount_percent > 0 && totals.total > 0 && (
                  <div className="mt-4 flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800" data-testid="early-discount-preview">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 text-emerald-700 font-bold text-[10px]">%</span>
                    <div>
                      Si el cliente paga en los próximos <b>{settings.early_payment_discount_days}</b> días recibirá <b>{settings.early_payment_discount_percent}% de descuento</b>{' '}
                      (<span className="numeric">{fmtMoney(totals.total * (settings.early_payment_discount_percent / 100))}</span> menos → total <span className="numeric font-semibold">{fmtMoney(totals.total * (1 - settings.early_payment_discount_percent / 100))}</span>).
                    </div>
                  </div>
                )}

                {/* Tanda 8: Reminders selector */}
                <div className="mt-6 border-t pt-4">
                  <Label className="text-sm font-medium text-slate-900">{t('reminder_label')}</Label>
                  <p className="text-xs text-slate-500 mt-1 mb-3">{t('reminder_help')}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="reminder-cadence-group">
                    {[
                      { v: 'none', label: t('reminder_none') },
                      { v: 'daily', label: t('reminder_daily') },
                      { v: 'weekly', label: t('reminder_weekly') },
                      { v: 'biweekly', label: t('reminder_biweekly') },
                      { v: 'monthly', label: t('reminder_monthly') },
                    ].map(o => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setReminderCadence(o.v)}
                        data-testid={`reminder-${o.v}`}
                        className={`text-center rounded-md border px-3 py-2 text-sm transition-colors ${reminderCadence === o.v ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step controls */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0 || busy} data-testid="wizard-back-button">
              <ChevronLeft className="h-4 w-4 mr-1" /> {t('qr_back')}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => canNext() && setStep(s => Math.min(STEPS.length - 1, s + 1))} disabled={!canNext()} data-testid="wizard-next-button">
                {t('qr_next')} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={generateReceipt} disabled={busy || !canNext()} data-testid="generate-receipt-button">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('qr_generate')}
              </Button>
            )}
          </div>
        </div>

        {/* Right: Live summary */}
        <div className="lg:col-span-1">
          <Card className="lg:sticky lg:top-20">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Resumen</div>
              {selectedClient ? (
                <div className="mb-3">
                  <div className="font-semibold text-slate-900 text-sm">{selectedClient.full_name}</div>
                  <div className="text-xs text-slate-500">{selectedClient.email || '—'}</div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 mb-3">Ningún cliente seleccionado</div>
              )}
              <div className="space-y-1.5 text-sm mb-4">
                {items.length === 0 && <div className="text-xs text-slate-400">Sin servicios</div>}
                {items.map((it, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="truncate mr-2">{it.quantity}× {it.service_name}</span>
                    <span className="numeric text-slate-600">{fmtMoney((it.quantity || 0) * (it.unit_price || 0) - (it.discount || 0))}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="numeric">{fmtMoney(totals.subtotal)}</span></div>
                {totals.disc > 0 && <div className="flex justify-between"><span className="text-slate-500">Descuento</span><span className="numeric text-rose-700">-{fmtMoney(totals.disc)}</span></div>}
                {totals.tax > 0 && totals.taxState > 0 && (
                  <div className="flex justify-between"><span className="text-slate-500">{settings?.tax_state_label || 'IVU Estatal'} ({(totals.stateRate*100).toFixed(1)}%)</span><span className="numeric">{fmtMoney(totals.taxState)}</span></div>
                )}
                {totals.tax > 0 && totals.taxMuni > 0 && (
                  <div className="flex justify-between"><span className="text-slate-500">{settings?.tax_municipal_label || 'IVU Municipal'} ({(totals.muniRate*100).toFixed(1)}%)</span><span className="numeric">{fmtMoney(totals.taxMuni)}</span></div>
                )}
                {totals.tax > 0 && totals.taxState === 0 && totals.taxMuni === 0 && (
                  <div className="flex justify-between"><span className="text-slate-500">{settings?.tax_name}</span><span className="numeric">{fmtMoney(totals.tax)}</span></div>
                )}
                <div className="flex justify-between font-semibold text-base pt-1"><span>Total</span><span className="numeric" data-testid="wizard-total">{fmtMoney(totals.total)}</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showCreateClient} onOpenChange={setShowCreateClient}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('clients_new')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t('client_full_name')} *</Label><Input value={newClient.full_name} onChange={(e) => setNewClient({...newClient, full_name: e.target.value})} data-testid="new-client-name" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input value={newClient.email} onChange={(e) => setNewClient({...newClient, email: e.target.value})} data-testid="new-client-email" className="mt-1.5" /></div>
              <div><Label>{t('client_phone')}</Label><Input value={newClient.phone} onChange={(e) => setNewClient({...newClient, phone: e.target.value})} data-testid="new-client-phone" className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('client_matter')}</Label><Input value={newClient.client_matter_id} onChange={(e) => setNewClient({...newClient, client_matter_id: e.target.value})} data-testid="new-client-matter" className="mt-1.5" /></div>
              <div><Label>{t('client_case')}</Label><Input value={newClient.case_name} onChange={(e) => setNewClient({...newClient, case_name: e.target.value})} data-testid="new-client-case" className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateClient(false)}>{t('cancel')}</Button>
            <Button onClick={createClientSubmit} data-testid="new-client-save">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
